import type { ReactNode } from 'react';
import { Text } from '@repletoryreach/sdk-ui';
import { TenantName } from '../../../components/TenantName';
import { TwoLineCell } from '../../../components/TwoLineCell';
import { useTenantMap } from '../../../data/useTenants';
import { formatDateTime, formatMinor, formatRelative } from '../../../lib/format';
import { useT } from '../../../i18n/I18nProvider';
import './billing.css';

/**
 * The organisation behind a money row: the name on top, the slug beneath it.
 *
 * Two tenants called "Acme" are told apart by their slug and by nothing else,
 * and support quotes the slug rather than the GUID, so the slug earns the
 * second line. The id stays where `TenantName` keeps it, in the tooltip.
 */
export function OrgCell({ id }: { id: string | null | undefined }) {
  const { map } = useTenantMap();
  const slug = id ? map.get(id)?.slug : undefined;
  return <TwoLineCell primary={<TenantName id={id} />} secondary={slug} />;
}

/**
 * A moment and how long ago it was. The exact value stays in the `title`, so a
 * relative line never costs the operator the timestamp they came for.
 */
export function WhenCell({ iso }: { iso: string }) {
  return (
    <span title={iso}>
      <TwoLineCell primary={formatDateTime(iso)} secondary={formatRelative(iso)} />
    </span>
  );
}

/**
 * Money, right-aligned, red when it leaves the wallet and green when it enters.
 *
 * `minor` is always in minor units and always goes through `formatMinor`. The
 * component exists so no billing page can format an amount its own way, which
 * is how "999 ZAR" for R9.99 got onto the ledger in the first place.
 */
const MONEY_TONE_CLASS = { debit: 'rr-billing-money--debit', credit: 'rr-billing-money--credit' };

export function Money({ minor, currency, signed = false, tone }: {
  minor: number;
  currency: string;
  /** Colour by the sign of the amount, which is how a journal reads. */
  signed?: boolean;
  /**
   * Colour explicitly, for a column whose heading already says the direction
   * and whose figure is therefore a positive magnitude.
   */
  tone?: 'debit' | 'credit';
}) {
  const cls = tone
    ? MONEY_TONE_CLASS[tone]
    : !signed || minor === 0 ? null : minor < 0 ? MONEY_TONE_CLASS.debit : MONEY_TONE_CLASS.credit;
  return <span className={['rr-billing-money', cls].filter(Boolean).join(' ')}>{formatMinor(minor, currency)}</span>;
}

export type EntryType = 'credit' | 'debit' | 'adjustment';

/**
 * What kind of entry this is, in the operator's words.
 *
 * The API's `entry_type_id` is a GUID, and a GUID is never a badge label. The entry's type code says it when the row
 * carries one: only an adjustment is an adjustment, and a top-up or a grant (no SKU either) is a credit (15 October
 * 2026; every SKU-less row used to read "adjustment"). Otherwise the sign says which way the money moved.
 */
export function entryTypeOf(entry: { amount_minor: number; sku_code: string | null; entry_type?: string | null }): EntryType {
  if (entry.entry_type ? entry.entry_type === 'adjustment' : entry.sku_code == null) return 'adjustment';
  return entry.amount_minor < 0 ? 'debit' : 'credit';
}

const TYPE_TOKEN: Record<EntryType, string> = {
  credit: 'var(--rr-color-success)',
  debit: 'var(--rr-color-error)',
  adjustment: 'var(--rr-color-warning)'
};

export function EntryTypeBadge({ type }: { type: EntryType }) {
  const t = useT();
  const accent = TYPE_TOKEN[type];
  const label = type === 'credit'
    ? t('billing.ledger.type.credit')
    : type === 'debit'
      ? t('billing.ledger.type.debit')
      : t('billing.ledger.type.adjustment');
  return (
    <span
      className="rr-billing-type"
      style={{
        color: accent,
        borderColor: `color-mix(in srgb, ${accent} 35%, transparent)`,
        background: `color-mix(in srgb, ${accent} 10%, transparent)`
      }}
    >
      {label}
    </span>
  );
}

/** A muted dash for a cell with nothing in it, so an empty column still reads as a column. */
export function Blank(): ReactNode {
  return <Text role="bodySmall" c="var(--rr-color-text-muted)">–</Text>;
}
